import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';

import { sourceOf } from '@/features/scheduling';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { LoadingState, UnavailableState } from '@/shared/ui/States';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { useComputeScores } from '@/features/scoring';

import {
  ruleVersionsQuery,
  useExecutionFindings,
  useExecutionTasks,
  useExecutions,
  useRules,
} from '../api/rules';
import type { Execution, Task } from '../api/rules';
import {
  outcomeKey,
  outcomeTone,
  runOutcome,
  runScope,
  runStats,
  shortRunId,
  startedLabel,
  taskDuration,
  taskStateKey,
  taskTarget,
} from '../model/presentation';
import { ViolationsPanel } from './ViolationsPanel';

/* One run: the four figures, the ledger of what each task did, and the violations behind it.
 *
 * **This exists because "View violations" appeared to do nothing.** It was a toggle that expanded a
 * panel *after* the loop rendering every execution card, so on a tenant with a handful of runs the
 * thing a reader had just asked for opened below the fold — the click scrolled nothing, revealed
 * nothing, and the panel was working perfectly the whole time. A page is the fix rather than a
 * scroll-into-view, for three reasons that outlast the bug: a run is a thing somebody links to, the
 * violations need room, and an expanded row makes the list mean a summary and a detail at once.
 *
 * **The run is read from the list rather than by id**, and that is a stated limitation rather than a
 * choice: there is no `GET /v1/rules/executions/{id}` in this client's reach, so this finds the run
 * in the same bounded listing the Executions screen reads — one cached request, shared. A run older
 * than that window is reported as not found *with the reason*: it says the listing does not reach
 * back that far rather than implying the run never existed.
 */

/** How many rule versions this page will resolve names for.
 *
 * A run's ledger names the *rule*, and a `Task` carries a rule **version** id. One request per
 * distinct version is the only route from one to the other, so the fan-out is bounded and the bound
 * is reported: past it the cell falls back to the target, which is still a true statement about what
 * the task measured. `DatasetDetail` bounds the same join at the same number for the same reason.
 */
const MAX_RULE_LOOKUPS = 12;
const RULE_VERSION_STALE_MS = 5 * 60 * 1000;

export function ExecutionDetail(): ReactElement {
  const { t } = useTranslation();
  const { executionId = '' } = useParams<{ executionId: string }>();
  const executions = useExecutions();
  const findings = useExecutionFindings();
  const tasks = useExecutionTasks(executionId);
  /* Which task's violations are open. `null` is the state the page loads in — nothing selected —
     and it is a third state rather than "the first task", because opening a violations table nobody
     asked for is what the previous version did. */
  const [opened, setOpened] = useState<string | null>(null);
  /* Which task's failure is open, and the rule it was for. Separate from `opened` rather than one
     selection with a mode, because the two answer different questions and a reader can want both at
     once on a run that found violations *and* had a task break.

     The rule name travels with the id because the ledger has already resolved it — a bounded fan-out
     over rule versions — and resolving it again here would be a second answer to the same question
     from a second copy of the same join. */
  const [failed, setFailed] = useState<{
    readonly taskId: string;
    readonly ruleName: string | undefined;
  } | null>(null);

  const execution = executions.data?.items.find((each) => each.id === executionId);
  const runFindings = findings.data?.items.find(
    (each) => each.execution_id === executionId,
  );

  if (executions.isPending) {
    return <LoadingState rows={4} label={t('execution.loading')} />;
  }

  if (executions.isError) {
    return (
      <UnavailableState
        title={t('execution.unreadable')}
        guidance={executions.error.message}
      />
    );
  }

  if (execution === undefined) {
    /* **"Not in the listing", never "does not exist".** The two are different facts and only one of
       them is known here — see the note above about there being no read by id. */
    return (
      <>
        <PageHeader
          title={t('execution.notFound.title')}
          lede={t('execution.notFound.lede')}
        />
        <Link to={SCREEN_PATHS.executions}>{t('execution.back')}</Link>
      </>
    );
  }

  const outcome = runOutcome(execution, runFindings);

  return (
    <>
      {/* The breadcrumb, and the only way back — this page is only ever arrived at from the list. */}
      <nav className="lq-breadcrumbs" aria-label={t('execution.breadcrumb')}>
        <Link to={SCREEN_PATHS.executions}>{t('nav.executions')}</Link>
        <span aria-hidden="true">/</span>
        <span className="lq-mono">{shortRunId(executionId)}</span>
      </nav>

      <div className="lq-run__head">
        <div style={{ flex: 1 }}>
          {/* The run's name in the mono face, as the reference sets it: this is an identifier, not a
              title about a subject. */}
          <h1 className="lq-run__title">{shortRunId(executionId)}</h1>
          <RunSummary execution={execution} />
          {/* **Why this run happened, under the summary that says what it did.** The same three
              treatments the ledger uses, and the same reason: a nightly schedule, a harvest that
              finished minutes earlier and somebody pressing a button send a reader to three
              different places, and a run detail with no origin leaves "who started this" answerable
              only from the audit chain. */}
          <p className="lq-run__source">
            {t('scheduling.source.detail', {
              label:
                sourceOf(execution.requested_by) === 'unknown'
                  ? t('scheduling.source.unknown')
                  : execution.requested_by,
            })}
          </p>
        </div>
        <StatusBadge
          shape="square"
          tone={outcomeTone(outcome)}
          label={
            outcome.kind === 'violations'
              ? t('executions.outcome.violations', { count: outcome.count })
              : t(outcomeKey(outcome))
          }
          title={t(`${outcomeKey(outcome)}Meaning`)}
        />
      </div>

      {/* The failure banner, and it renders only when the server said something. `detail` is the
          platform's own words — the one thing that explains a failed run — so it is on the page
          rather than in a tooltip. A left rule rather than a full border, matching the incident
          cards: it marks a thing that happened rather than a section of the page. */}
      {execution.detail !== null && outcome.kind === 'failed' ? (
        <div className="lq-run__alert" role="alert">
          <strong className="lq-run__alert-title">{t('execution.alert.title')}</strong>
          <p className="lq-run__alert-body">{execution.detail}</p>
        </div>
      ) : null}

      <div className="lq-run__rule" />

      <RunStats execution={execution} findings={runFindings} />

      <QualityScoreAction execution={execution} />

      <h2 className="lq-run__section">{t('execution.tasks')}</h2>
      <TaskLedger
        executionId={executionId}
        tasks={tasks}
        onOpen={setOpened}
        onFailure={(taskId, ruleName) => {
          setFailed({ taskId, ruleName });
        }}
      />
      <p className="lq-run__note">{t('execution.tasks.note')}</p>

      {/* The reason a task could not run, on the page. `detail` is the platform's own sentence — the
          only thing that explains it — and it was previously reachable only by hovering. */}
      {failed === null ? null : (
        <FailureReason
          task={tasks.data?.items.find((each) => each.id === failed.taskId)}
          ruleName={failed.ruleName}
          onClose={() => {
            setFailed(null);
          }}
        />
      )}

      {/* **Opened from a task, which is the reference's own interaction** — its ledger puts "View
          violations" on the rows that found some and nothing on the rows that did not, because there
          is nothing to open for a clean task.

          Not unconditional, which is a change from the first version of this page. That one rendered
          the panel always, on the reasoning that this page *is* the answer to "show me the
          violations" — true of the page and not of every row, and it put a full violations table
          under a run whose six tasks were all clean. The anchor scrolls to it, so the click still
          reveals something on screen, which is the defect this whole page exists to fix. */}
      {opened === null ? null : (
        <section id="execution-violations" className="lq-run__violations">
          <h2 className="lq-run__section">{t('execution.violations')}</h2>
          <ViolationsPanel executionId={executionId} execution={execution} />
        </section>
      )}
    </>
  );
}

/** One line saying what kind of run this was and what it covered.
 *
 * The reference reads *"Scheduled run · daily 06:00 group, re-triggered by A. Rao at 10:02 ·
 * core-banking-pg"*. Two of those three are things this platform does not record — an execution
 * carries no schedule identity and no re-trigger provenance — so they are **not invented**: the line
 * says what is true, which is the strategy, the dialect and what the run covered. Recorded here
 * rather than left as a gap, because a plausible-looking "Scheduled run" on a manually-triggered run
 * would be the screen asserting something no field supports.
 */
function RunSummary({ execution }: { readonly execution: Execution }): ReactElement {
  const { t } = useTranslation();
  const scope = runScope(execution);
  const covered =
    scope.kind === 'one'
      ? scope.name
      : scope.kind === 'many'
        ? t('executions.scope.many', { count: scope.count })
        : t('executions.scope.unresolved');
  const started = startedLabel(execution.started_at, new Date());
  return (
    <p className="lq-run__summary">
      {[
        t(`executions.strategy.${execution.strategy}`),
        covered,
        execution.dialect,
        started ?? t('executions.queued'),
      ].join(' · ')}
    </p>
  );
}

/** Tasks, rows read, violations, elapsed — with a sentence under each saying what it covers. */
function RunStats({
  execution,
  findings,
}: {
  readonly execution: Execution;
  readonly findings:
    | {
        readonly violations: number | null;
        readonly rows_evaluated: number | null;
        readonly measured_tasks: number;
      }
    | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const elapsed = taskDuration({
    started_at: execution.started_at,
    finished_at: execution.finished_at,
  });
  const stats = runStats(execution.progress, findings, elapsed);

  return (
    <div className="lq-runstats">
      {stats.map((stat) => (
        <div className="lq-runstat" key={stat.key}>
          <span className="lq-runstat__label">{t(`execution.stat.${stat.key}`)}</span>
          <span
            className={
              stat.value === null
                ? 'lq-runstat__value lq-runstat__value--absent'
                : stat.tone === undefined
                  ? 'lq-runstat__value'
                  : `lq-runstat__value lq-runstat__value--${stat.tone}`
            }
          >
            {/* An em dash at the figure's own size, so a row of four still scans as four figures
                rather than one of them having failed to render. */}
            {stat.value ?? '—'}
          </span>
          <span className="lq-runstat__note">{stat.note}</span>
        </div>
      ))}
    </div>
  );
}

/** Turns this run's findings into a score, on the run whose `may_publish_a_score` permits it.
 *
 * **This is the verdict `may_publish_a_score` exists to lead to, and it had nowhere to lead.**
 * `useComputeScores` and its copy (`execution.score.*`) were built and never called from any
 * screen — the hook's own barrel export says why: it was private to a since-removed
 * paste-an-execution-id form, and stayed private after that form went, which is what left the
 * verdict this run already carries with no action attached to it.
 *
 * **Idempotent by design, so a second click is harmless rather than merely tolerated.**
 * `ScoringService.compute` returns the already-stored scores for a run that has been scored
 * before instead of recomputing under whatever model happens to be active now — recomputing
 * would let a retried click silently republish different numbers under an unchanged run. That is
 * why this needs no "already scored" check of its own: the button is safe to show and safe to
 * press again, and the result underneath it is always the true current answer either way.
 *
 * Absent entirely when the run may not publish a score, rather than shown disabled — `execution.
 * score.notPermitted` says why in words, which is more useful than a control a reader cannot
 * tell is unavailable from one that is merely slow.
 */
function QualityScoreAction({
  execution,
}: {
  readonly execution: Execution;
}): ReactElement {
  const { t } = useTranslation();
  const compute = useComputeScores();

  return (
    <section className="lq-run__score" aria-labelledby="execution-score-heading">
      <h2 className="lq-run__section" id="execution-score-heading">
        {t('execution.score.title')}
      </h2>
      {execution.may_publish_a_score ? (
        <>
          <p className="lq-muted" title={t('execution.score.permittedMeaning')}>
            {t('execution.score.permitted')}
          </p>
          <Button
            tone="primary"
            busy={compute.isPending}
            title={t('execution.score.computeMeaning')}
            onClick={() => {
              compute.mutate(execution.id);
            }}
          >
            {t('execution.score.compute')}
          </Button>
          {compute.isSuccess ? (
            <p className="lq-run__score-result" role="status">
              {t('execution.score.done', { count: compute.data.items.length })}
            </p>
          ) : null}
          {compute.isError ? (
            <p className="lq-run__score-result lq-run__score-result--error" role="alert">
              {t('execution.score.error')}
            </p>
          ) : null}
        </>
      ) : (
        <p className="lq-muted">{t('execution.score.notPermitted')}</p>
      )}
    </section>
  );
}

/** What each task measured, in the design's seven columns. */
function TaskLedger({
  executionId,
  tasks,
  onOpen,
  onFailure,
}: {
  readonly executionId: string;
  readonly tasks: ReturnType<typeof useExecutionTasks>;
  readonly onOpen: (taskId: string) => void;
  readonly onFailure: (taskId: string, ruleName: string | undefined) => void;
}): ReactElement {
  const { t } = useTranslation();
  const rules = useRules('');

  /* A run's ledger names the rule; a `Task` carries a rule *version* id. Bounded and reported — see
     `MAX_RULE_LOOKUPS`. Sorted so the query keys are stable across renders and React Query does not
     see a different fan-out every time the task list refetches. */
  const versionIds = useMemo(() => {
    const unique = [
      ...new Set((tasks.data?.items ?? []).map((task) => task.rule_version_id)),
    ];
    return unique.sort().slice(0, MAX_RULE_LOOKUPS);
  }, [tasks.data]);

  const ruleIds = useMemo(
    () => (rules.data?.items ?? []).map((rule) => rule.id).sort(),
    [rules.data],
  );

  const versionQueries = useQueries({
    queries: ruleIds.map((ruleId) => ({
      ...ruleVersionsQuery(ruleId),
      staleTime: RULE_VERSION_STALE_MS,
      enabled: versionIds.length > 0,
    })),
  });

  /* Version id to the rule's name. Built from every rule's versions, because the version → rule
     direction is not a route this API has: the only way across is to read each rule's versions and
     invert. */
  const ruleNameByVersion = useMemo(() => {
    const names = new Map<string, string>();
    const rulesById = new Map(
      (rules.data?.items ?? []).map((rule) => [rule.id, rule.name]),
    );
    for (const query of versionQueries) {
      for (const version of query.data?.items ?? []) {
        const name = rulesById.get(version.rule_id);
        if (name !== undefined) names.set(version.id, name);
      }
    }
    return names;
  }, [versionQueries, rules.data]);

  if (tasks.isPending)
    return <LoadingState rows={3} label={t('execution.tasks.loading')} />;
  if (tasks.isError) {
    return (
      <UnavailableState
        title={t('execution.tasks.unreadable')}
        guidance={tasks.error.message}
      />
    );
  }
  if (tasks.data.items.length === 0) {
    /* A zero-task run is a real state worth naming rather than an empty tbody: the route refuses a
       plan covering nothing, so seeing it means something upstream changed after the plan was made. */
    return (
      <EmptyState
        title={t('execution.task.none')}
        guidance={t('execution.task.noneWhy')}
      />
    );
  }

  return (
    <DataTable
      variant="ledger"
      caption={t('execution.tasks')}
      columns={[
        { key: 'rule', label: t('execution.task.rule') },
        { key: 'target', label: t('execution.task.target') },
        { key: 'rows', label: t('execution.task.rows'), numeric: true },
        { key: 'violations', label: t('execution.task.violations'), numeric: true },
        { key: 'duration', label: t('execution.task.duration') },
        { key: 'state', label: t('execution.task.state'), actions: true },
        { key: 'act', label: '', actions: true },
      ]}
    >
      {tasks.data.items.map((task) => (
        <TaskRow
          key={task.id}
          task={task}
          executionId={executionId}
          ruleName={ruleNameByVersion.get(task.rule_version_id)}
          onOpen={onOpen}
          onFailure={onFailure}
        />
      ))}
    </DataTable>
  );
}

function TaskRow({
  task,
  ruleName,
  onOpen,
  onFailure,
}: {
  readonly task: Task;
  readonly executionId: string;
  readonly ruleName: string | undefined;
  readonly onOpen: (taskId: string) => void;
  readonly onFailure: (taskId: string, ruleName: string | undefined) => void;
}): ReactElement {
  const { t } = useTranslation();
  const measured = task.state === 'succeeded';
  const duration = taskDuration(task);

  return (
    /* The failed row is tinted, and the state is also in words in its own column — the tint is the
       scan, not the information. */
    <tr className={task.state === 'failed' ? 'lq-row--failed' : undefined}>
      <td>
        {/* The rule's name where the bounded lookup reached it, the target where it did not. Never a
            version UUID: that is an identifier no screen hands a reader, and it would be the
            bare-UUID defect this codebase has shipped four times. */}
        {ruleName ?? <span className="lq-muted">{t('execution.task.ruleUnknown')}</span>}
      </td>
      <td className="lq-mono lq-cell--quiet">{taskTarget(task)}</td>
      <td className="lq-table__num">
        {/* **An em dash, not a zero, for a task that never read anything.** A failed task's
            `rows_evaluated` is 0 because it did not run; rendering that as a measured zero is the
            distinction the whole screen is built to hold. */}
        {measured ? task.rows_evaluated.toLocaleString() : '—'}
      </td>
      <td className="lq-table__num">
        {measured ? (
          <span
            className={task.violation_count > 0 ? 'lq-count--found' : 'lq-count--none'}
          >
            {task.violation_count.toLocaleString()}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="lq-cell--quiet">{duration ?? '—'}</td>
      <td className="lq-table__actions">
        <span className={`lq-taskstate lq-taskstate--${task.state}`}>
          {t(taskStateKey(task.state))}
        </span>
      </td>
      <td className="lq-table__actions">
        {/* The reference offers "View violations" on a task that found some and "Why it failed" on
            one that broke — and nothing on a clean task, because there is nothing to open. Both
            resolve to the same place today: the violations panel below, and the task's own `detail`
            for a failure. */}
        {measured && task.violation_count > 0 ? (
          <a
            href="#execution-violations"
            onClick={() => {
              onOpen(task.id);
            }}
          >
            {t('execution.task.viewViolations')}
          </a>
        ) : task.state === 'failed' ? (
          /* **A real control, not a tooltip.** It was a `title=` — the reason was there and
             invisible, reachable only by hovering a phrase that does not look hoverable, and lost
             entirely on a touch screen. "Why it failed" is the question a reader clicks, so it opens
             the reason on the page. Offered even when `detail` is null, because "we recorded no
             reason" is itself the answer to the question and a missing link would leave a reader
             hunting for one. */
          <button
            type="button"
            className="lq-linkish"
            onClick={() => {
              onFailure(task.id, ruleName);
            }}
          >
            {t('execution.task.whyItFailed')}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

/** Why one task could not run, in a dialog.
 *
 * **A dialog rather than a banner under the table**, which is where this started: the reason appeared
 * below the footnote, several hundred pixels from the row that raised it, and on a run with six tasks
 * a reader had to work out which one it was about. A failure is a thing a reader stops to read, so it
 * gets the treatment the product already uses for "stop and read this" — focus trapped, Escape
 * closes, the rest of the page inert behind it.
 *
 * Named by the **rule** rather than the target, because that is what the reader clicked next to.
 *
 * The body carries two paragraphs and both are necessary. The platform's own sentence says what went
 * wrong; the second says what it means for the dash in the violations column — the rule never reached
 * the data, so this is not a count of zero. Without that, a reader reads the dash as a zero, which is
 * the whole distinction this screen is built to hold.
 */
function FailureReason({
  task,
  ruleName,
  onClose,
}: {
  readonly task: Task | undefined;
  readonly ruleName: string | undefined;
  readonly onClose: () => void;
}): ReactElement | null {
  const { t } = useTranslation();
  if (task === undefined) return null;

  return (
    <Modal
      open
      size="narrow"
      onClose={onClose}
      title={t('execution.task.failureTitle', { rule: ruleName ?? taskTarget(task) })}
      hint={t('execution.task.failureHint', { target: taskTarget(task) })}
    >
      {/* The server's sentence verbatim where there is one. `SourceExecutionError` deliberately
          carries no driver message — it names the rule kind and the exception class and nothing else
          — so this cannot put a statement or a tenant's own literals on screen. Mono, because what it
          contains is an error class and a host, which are identifiers. */}
      <p className="lq-diagnostic">{task.detail ?? t('execution.task.failureUnknown')}</p>
      <p className="lq-muted">{t('execution.task.failureNote')}</p>
      {/* `.lq-confirm__actions`, the kit's action row — not a `lq-modal__actions` of my own, which is
          what I first wrote and which is declared in no stylesheet. **No gate catches a class that
          exists nowhere**: `stylesheets.test.ts` asserts a class is declared in exactly one file and
          that no `var()` names an undeclared property, and it cannot see a name a component invents.
          Recorded rather than only fixed — it is the third time in this session. */}
      <div className="lq-confirm__actions">
        <Button tone="secondary" onClick={onClose}>
          {t('execution.task.close')}
        </Button>
      </div>
    </Modal>
  );
}
